import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { useNotification } from "@/shared/hooks/useNotification";
import { useRequestReserveRelease } from "../../hooks/useReserveRelease";

export interface ReleaseReserveDialogEntry {
  entryId: string;
  cropTypeName: string;
  variety?: string;
  fillingReserve: number;
  stockUnit: string;
}

interface ReleaseReserveDialogProps {
  entry: ReleaseReserveDialogEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ReleaseReserveDialog = ({ entry, open, onOpenChange }: ReleaseReserveDialogProps) => {
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");

  const mutation = useRequestReserveRelease();
  const notify = useNotification();

  useEffect(() => {
    if (!open) {
      setQuantity("");
      setReason("");
    }
  }, [open]);

  const maxQty = entry.fillingReserve;
  const trimmedReason = reason.trim();
  const parsedQty = parseFloat(quantity);
  const isValid = parsedQty > 0 && parsedQty <= maxQty && trimmedReason.length > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    mutation.mutate(
      { entryId: entry.entryId, body: { quantity: parsedQty, reason: trimmedReason } },
      {
        onSuccess: () => {
          notify.success("Reserve release submitted — pending GM approval");
          onOpenChange(false);
        },
        onError: (err: any) => {
          notify.error(err?.response?.data?.message ?? "Failed to submit reserve release");
        },
      }
    );
  };

  const unit = entry.stockUnit?.toLowerCase() ?? "units";

  return (
    <Dialog open={open} onOpenChange={(v) => !mutation.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Release Filling Reserve</DialogTitle>
          <DialogDescription>
            {entry.cropTypeName}
            {entry.variety ? ` — ${entry.variety}` : ""} · Filling reserve available: {" "}
            <strong>{maxQty} {unit}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel htmlFor="reserveQty">
              Quantity to Release <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              id="reserveQty"
              type="number"
              step="0.01"
              min="0.01"
              max={maxQty}
              placeholder={`Up to ${maxQty}`}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={mutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              Cannot exceed the current filling reserve ({maxQty} {unit}).
            </p>
          </Field>

          <Field>
            <FieldLabel htmlFor="reserveReason">
              Reason / Justification <span className="text-destructive">*</span>
            </FieldLabel>
            <Textarea
              id="reserveReason"
              rows={4}
              placeholder="e.g. Batch healthy, no disease — releasing balance to sale"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={mutation.isPending}
              maxLength={1000}
            />
          </Field>

          <p className="text-xs text-muted-foreground">
            This request will be sent to the General Manager for approval. On approval, the
            released quantity will move from <em>Filling Reserve</em> to <em>Total Quantity</em>
            and become available for sale.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || mutation.isPending}>
            {mutation.isPending ? "Submitting..." : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
